// Pop-up de bom-dia das horas (01/10/2026).
//
// Pedido do Lucas: "Talvez possa aparecer um pop-up para o usuário (talvez para
// todos) a primeira vez que o mouse dele se movimenta naquele dia (os
// computadores tendem a ficar ligados) para que ele inicie registrando as horas
// dele". Administrativo e Comercial recebem o centro de custo do setor; a
// operação, o destino mais usado nos últimos 7 dias; quem tem rateio fixo no
// cadastro não vê nada.
//
// Uma pergunta por pessoa por dia. Quem decide SE e O QUÊ sugerir é o servidor
// (GET /financeiro/horas/sugestao-do-dia); aqui só se espera a primeira mexida
// do dia e se liga o cronômetro pela rota de sempre. Sem resposta do servidor,
// não aparece nada — atalho de horas não incomoda com erro de rede.
import { useCallback, useEffect, useRef, useState } from 'react';
import { App as AntApp, Button, Modal, Typography } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import { getToken } from '../utils/auth';
import { api } from '../pages/operacional-horas/api';
import { EVENTO_HORAS } from './HorasAtalho';
import {
  CHAVE_BOM_DIA, ESPERA_APOS_FALHA_MS, corpoDoCronometro, deveAbrir, diaLocal, emailDoToken, jaPerguntouHoje,
  lerRegistro, motivoDaSugestao, saudacao, type SugestaoDoDia,
} from './sugestaoDoDia';

const { Text } = Typography;

export default function HorasBomDia({ onEscolher }: { onEscolher: () => void }) {
  const { message } = AntApp.useApp();
  const [sugestao, setSugestao] = useState<SugestaoDoDia | null>(null);
  const [ligando, setLigando] = useState(false);
  // `diaVisto`: o dia em que já resolvemos (perguntou ou não precisava). `proxima`:
  // antes disso não tenta de novo (falha de rede). Em ref: o mouse dispara o tempo todo.
  const estado = useRef({ consultando: false, diaVisto: '', proxima: 0 });

  const conferir = useCallback(async () => {
    const st = estado.current;
    const dia = diaLocal(new Date());
    if (st.consultando || st.diaVisto === dia || Date.now() < st.proxima) return;
    st.consultando = true;
    try {
      const email = emailDoToken(await getToken()) || '';
      let registro = null;
      try { registro = lerRegistro(window.localStorage.getItem(CHAVE_BOM_DIA)); } catch { /* sem armazenamento */ }
      if (email && jaPerguntouHoje(registro, dia, email)) { st.diaVisto = dia; return; }
      const d: SugestaoDoDia = await api('/financeiro/horas/sugestao-do-dia');
      st.diaVisto = dia;
      try {
        window.localStorage.setItem(CHAVE_BOM_DIA, JSON.stringify({ dia, email: d.usuario_email || email }));
      } catch { /* sem armazenamento: pergunta de novo se recarregar, e só */ }
      if (deveAbrir(d)) setSugestao(d);
    } catch {
      st.proxima = Date.now() + ESPERA_APOS_FALHA_MS;
    } finally { st.consultando = false; }
  }, []);

  useEffect(() => {
    const visivel = () => { if (document.visibilityState === 'visible') conferir(); };
    const eventos = ['mousemove', 'keydown', 'pointerdown'] as const;
    eventos.forEach((e) => window.addEventListener(e, conferir, { passive: true }));
    document.addEventListener('visibilitychange', visivel);
    return () => {
      eventos.forEach((e) => window.removeEventListener(e, conferir));
      document.removeEventListener('visibilitychange', visivel);
    };
  }, [conferir]);

  async function iniciar() {
    if (!sugestao?.destino) return;
    setLigando(true);
    try {
      await api('/financeiro/horas/cronometro/iniciar', { method: 'POST', body: JSON.stringify(corpoDoCronometro(sugestao.destino)) });
      message.success(`Cronômetro ligado em ${sugestao.destino.rotulo}.`);
      setSugestao(null);
      try { window.dispatchEvent(new Event(EVENTO_HORAS)); } catch { /* sem window */ }
    } catch (e: any) {
      // A recusa do servidor é frase para gente (ausência, cronômetro já ligado em outra aba…).
      message.error(e?.message || 'Não foi possível ligar o cronômetro.', 8);
    } finally { setLigando(false); }
  }

  function escolher() {
    setSugestao(null);
    onEscolher();
  }

  if (!sugestao) return null;
  const { destino } = sugestao;
  return (
    <Modal
      open centered width={440} footer={null} maskClosable={false}
      title={saudacao(new Date().getHours(), sugestao.nome)}
      onCancel={() => setSugestao(null)}
    >
      <Text style={{ display: 'block', marginBottom: 14 }}>
        {destino ? 'Quer começar a anotar as horas de hoje por aqui?' : 'Vamos começar a anotar as horas de hoje?'}
      </Text>
      {destino && (
        <Button type="primary" size="large" block icon={<PlayCircleOutlined />} loading={ligando} onClick={iniciar}
          style={{ height: 'auto', minHeight: 48, padding: '8px 14px', whiteSpace: 'normal' }}>
          <span>Iniciar em <strong>{destino.rotulo}</strong></span>
        </Button>
      )}
      {/* O nome do contrato fica FORA do botão: título comprido fazia um botão de três linhas. */}
      {destino?.contrato_id && destino.detalhe && (
        <Text ellipsis={{ tooltip: destino.detalhe }} style={{ display: 'block', fontSize: 13, marginTop: 8 }}>{destino.detalhe}</Text>
      )}
      <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '6px 0 14px' }}>{motivoDaSugestao(sugestao)}</Text>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Button type={destino ? 'default' : 'primary'} onClick={escolher} disabled={ligando}>
          {destino ? 'Escolher outro' : 'Escolher onde começar'}
        </Button>
        <Button type="text" onClick={() => setSugestao(null)} disabled={ligando}>Agora não</Button>
      </div>
    </Modal>
  );
}
